import React, { useState } from 'react';
import { X, Heart, ChevronRight, Check, Send } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Questions for each game type
const GAME_QUESTIONS = {
  'get-to-know-you': [
    { q: "What's your idea of a perfect date?", options: ["Romantic dinner", "Adventure outdoors", "Movie night at home", "Dancing & nightlife"] },
    { q: "What do you value most in a relationship?", options: ["Trust", "Communication", "Romance", "Fun & laughter"] },
    { q: "How do you like to spend weekends?", options: ["Relaxing at home", "Going out with friends", "Trying new activities", "Quality time with partner"] },
    { q: "What's your love language?", options: ["Words of affirmation", "Quality time", "Physical touch", "Acts of service"] },
    { q: "What's your biggest dream?", options: ["Travel the world", "Build a family", "Career success", "Personal growth"] },
    { q: "Morning person or night owl?", options: ["Early bird", "Night owl", "Depends on the day", "Somewhere in between"] },
    { q: "How do you handle disagreements?", options: ["Talk it out calmly", "Need space first", "Find compromise quickly", "Avoid conflict"] },
    { q: "What makes you feel loved?", options: ["Surprises & gifts", "Undivided attention", "Physical affection", "Helpful gestures"] }
  ],
  'love-psychology-quiz': [
    { q: "When you're stressed, you prefer your partner to:", options: ["Listen and comfort", "Give practical advice", "Give you space", "Distract you with fun"] },
    { q: "Your attachment style is closest to:", options: ["Secure - I trust easily", "Anxious - I need reassurance", "Avoidant - I value independence", "Mixed - depends on the person"] },
    { q: "In love, you tend to:", options: ["Fall fast and hard", "Take time to open up", "Show love through actions", "Express feelings verbally"] },
    { q: "Your ideal relationship dynamic:", options: ["Equal partnership", "Traditional roles", "Flexible and fluid", "Passionate and intense"] },
    { q: "When hurt, you typically:", options: ["Express feelings directly", "Withdraw and process", "Seek support from others", "Try to move on quickly"] },
    { q: "You believe true love is:", options: ["Destined and magical", "Built through effort", "A choice every day", "A deep friendship"] },
    { q: "Your biggest relationship fear:", options: ["Being abandoned", "Losing independence", "Not being enough", "Getting hurt again"] },
    { q: "How do you show love?", options: ["Saying I love you often", "Planning special moments", "Being physically close", "Doing things for them"] }
  ],
  'compatibility-challenge': [
    { q: "Kids in the future?", options: ["Definitely yes", "Maybe someday", "Probably not", "Definitely no"] },
    { q: "Where would you live ideally?", options: ["Big city", "Suburbs", "Countryside", "Near the beach"] },
    { q: "How important is religion/spirituality?", options: ["Very important", "Somewhat important", "Not very important", "Not at all"] },
    { q: "Money management style:", options: ["Save everything", "Balance saving & spending", "Live for today", "Invest aggressively"] },
    { q: "Social life preference:", options: ["Large friend groups", "Few close friends", "Mostly just us", "Mix of both"] },
    { q: "Career vs relationship priority:", options: ["Career comes first", "Relationship comes first", "Equal balance", "Depends on the phase"] },
    { q: "How often should couples argue?", options: ["Never ideally", "Rarely is healthy", "Sometimes is normal", "Often means passion"] },
    { q: "Pet preferences:", options: ["Dogs", "Cats", "Both", "No pets"] }
  ]
};

const GAME_TITLES = {
  'get-to-know-you': { title: 'Get to Know You', emoji: '💬', color: 'from-blue-500 to-cyan-500' },
  'love-psychology-quiz': { title: 'Love & Psychology', emoji: '💕', color: 'from-pink-500 to-rose-500' },
  'compatibility-challenge': { title: 'Compatibility Challenge', emoji: '🧠', color: 'from-purple-500 to-indigo-500' }
};

const QuizGame = ({ 
  gameId, 
  matchId, 
  currentUser, 
  otherUser, 
  onClose, 
  onComplete,
  existingAnswers = null // If user B is responding to user A's answers
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [compatibilityScore, setCompatibilityScore] = useState(null);

  const questions = GAME_QUESTIONS[gameId] || GAME_QUESTIONS['get-to-know-you'];
  const gameInfo = GAME_TITLES[gameId] || GAME_TITLES['get-to-know-you'];
  
  const handleAnswer = (answerIndex) => {
    const newAnswers = [...answers, answerIndex];
    setAnswers(newAnswers);
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Quiz complete
      if (existingAnswers) {
        // Compare answers
        calculateCompatibility(newAnswers, existingAnswers);
      } else {
        setShowResults(true);
      }
    }
  };

  const calculateCompatibility = (myAnswers, theirAnswers) => {
    setComparing(true);
    let matches = 0;
    const total = Math.min(myAnswers.length, theirAnswers.length);
    
    for (let i = 0; i < total; i++) {
      if (myAnswers[i] === theirAnswers[i]) {
        matches++;
      }
    }
    
    const score = Math.round((matches / total) * 100);
    setCompatibilityScore(score);
    setShowResults(true);
    setComparing(false);
  };

  const handleShareResults = async () => {
    try {
      const resultMessage = existingAnswers 
        ? `[QUIZ_RESULT:${JSON.stringify({
            gameId,
            gameName: gameInfo.title,
            emoji: gameInfo.emoji,
            score: compatibilityScore,
            playerA: otherUser.name,
            playerB: currentUser.name
          })}]`
        : `[QUIZ_ANSWERS:${JSON.stringify({
            gameId,
            gameName: gameInfo.title,
            emoji: gameInfo.emoji,
            answers,
            playerName: currentUser.name
          })}]`;

      await axios.post(
        `${API}/matches/${matchId}/messages`,
        { text: resultMessage },
        { withCredentials: true }
      );

      toast.success(existingAnswers ? 'Results shared!' : 'Your answers were sent! Waiting for their response...');
      if (onComplete) onComplete();
      onClose();
    } catch (error) {
      toast.error('Failed to share results');
    }
  };

  if (showResults) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-in zoom-in-95">
          <div className="text-center">
            <div className="text-6xl mb-4">{gameInfo.emoji}</div>
            <h2 className={`text-2xl font-bold bg-gradient-to-r ${gameInfo.color} bg-clip-text text-transparent mb-2`}>
              {existingAnswers ? 'Compatibility Results!' : 'Quiz Complete!'}
            </h2>
            
            {existingAnswers && compatibilityScore !== null ? (
              <>
                <div className="my-6">
                  <div className="text-6xl font-bold text-purple-600">{compatibilityScore}%</div>
                  <p className="text-gray-600 mt-2">
                    {compatibilityScore >= 80 ? "Amazing match! 💖" :
                     compatibilityScore >= 60 ? "Great compatibility! 💕" :
                     compatibilityScore >= 40 ? "Good potential! 💛" :
                     "Opposites attract! 🌟"}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  You and {otherUser.name} matched on {Math.round(compatibilityScore / 100 * questions.length)} out of {questions.length} questions!
                </p>
              </>
            ) : (
              <p className="text-gray-600 mb-6">
                You answered {answers.length} questions! Share with {otherUser?.name} to see your compatibility.
              </p>
            )}

            <div className="space-y-3">
              <Button
                onClick={handleShareResults}
                className={`w-full bg-gradient-to-r ${gameInfo.color} text-white py-3`}
              >
                <Send className="w-4 h-4 mr-2" />
                {existingAnswers ? 'Share Results' : 'Send to Partner'}
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${gameInfo.color} p-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{gameInfo.emoji}</span>
              <span className="font-bold">{gameInfo.title}</span>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="mt-3 bg-white/30 rounded-full h-2">
            <div 
              className="bg-white rounded-full h-2 transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
            />
          </div>
          <p className="text-sm mt-2 opacity-90">
            Question {currentQuestion + 1} of {questions.length}
          </p>
        </div>

        {/* Question */}
        <div className="p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            {questions[currentQuestion].q}
          </h3>

          {/* Options */}
          <div className="space-y-3">
            {questions[currentQuestion].options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className="w-full p-4 text-left rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all flex items-center justify-between group"
              >
                <span className="text-gray-700 group-hover:text-purple-700">{option}</span>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizGame;
